(() => {
  const SELECTOR = '[data-moztech-rtf-story]';

  if (window.MoztechRtfStory) {
    window.MoztechRtfStory.initWithin(document);
    return;
  }

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const smoothstep = (value) => value * value * (3 - 2 * value);
  const lerp = (start, end, progress) => start + (end - start) * progress;

  class MoztechRtfStoryInstance {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.stageTrack = root.querySelector('[data-story-track]');
      this.stage = root.querySelector('.moz-rtf-story__stage');
      this.revealTrack = root.querySelector('[data-reveal-track]');
      this.revealStage = root.querySelector('.moz-rtf-story__reveal-sticky');
      this.number = root.querySelector('[data-story-number]');
      this.progressBar = root.querySelector('[data-story-progress]');
      this.fan = root.querySelector('.moz-rtf-story__fan');
      this.cards = Array.from(root.querySelectorAll('[data-story-card]'));
      this.surfaces = this.cards.map((card) => card.querySelector('[data-story-surface]'));
      this.links = this.cards.map((card) => card.querySelector('a'));
      this.stepButtons = Array.from(root.querySelectorAll('[data-story-jump]'));
      this.revealCopy = root.querySelector('[data-reveal-copy]');
      this.motionQuery = window.matchMedia('(min-width: 990px) and (prefers-reduced-motion: no-preference)');
      this.mobileMotionQuery = window.matchMedia('(max-width: 989px) and (prefers-reduced-motion: no-preference)');
      this.abortController = new AbortController();
      this.frame = 0;
      this.mobileFrame = 0;
      this.pointerFrame = 0;
      this.pendingPointerPoint = null;
      this.lastActiveIndex = -1;
      this.dragState = null;
      this.mobileObserver = null;
      this.viewportObserver = null;
      this.phaseObserver = null;
      this.preloadObserver = null;
      this.warmedCenters = new Set();
      this.queuedImages = new Set();
      this.imageWarmQueue = [];
      this.imageWarmActive = 0;
      this.maxConcurrentImageWarms = window.innerWidth < 990 ? 1 : 2;
      this.isNearViewport = !('IntersectionObserver' in window);
      this.stageNearViewport = !('IntersectionObserver' in window);
      this.revealNearViewport = false;
      this.staticApplied = false;
      this.lastStepIndex = -1;
      this.lastLitIndex = -1;
      this.cardInteractionStates = [];
      this.cardRenderStates = [];
      this.mobileCardCenters = null;
      this.activeCardRect = null;
      this.numberAnimation = null;
      this.revealPrepareHandle = 0;
      this.revealPrepareUsesIdle = false;
      this.scrollIdleTimer = 0;
      this.isPageScrolling = false;
      this.stageMetrics = null;
      this.revealMetrics = null;
      this.layoutObserver = null;
      this.layoutFrame = 0;

      this.measureTracks();
      this.scheduleRevealPreparation();
      this.bind();
      this.update();
    }

    measureTracks() {
      const scrollTop = window.scrollY || window.pageYOffset || 0;
      const measure = (element) => {
        if (!element) return null;
        const rect = element.getBoundingClientRect();
        return {
          top: rect.top + scrollTop,
          height: Math.max(rect.height, element.offsetHeight, 1),
        };
      };

      this.stageMetrics = measure(this.stageTrack);
      this.revealMetrics = measure(this.revealTrack);
    }

    scheduleRevealPreparation() {
      if (!this.revealCopy || this.revealCopy.dataset.split === 'true' || this.revealPrepareHandle) return;

      const prepare = () => {
        this.revealPrepareHandle = 0;
        this.prepareReveal();
      };

      if ('requestIdleCallback' in window) {
        this.revealPrepareUsesIdle = true;
        this.revealPrepareHandle = window.requestIdleCallback(prepare, { timeout: 4000 });
        return;
      }

      this.revealPrepareUsesIdle = false;
      this.revealPrepareHandle = window.setTimeout(prepare, 320);
    }

    prepareReveal() {
      if (!this.revealCopy || this.revealCopy.dataset.split === 'true') return;

      if (this.revealPrepareHandle) {
        if (this.revealPrepareUsesIdle && 'cancelIdleCallback' in window) {
          window.cancelIdleCallback(this.revealPrepareHandle);
        } else {
          window.clearTimeout(this.revealPrepareHandle);
        }
        this.revealPrepareHandle = 0;
      }

      const accessibleText = this.revealCopy.textContent.trim();
      if (accessibleText) this.revealCopy.setAttribute('aria-label', accessibleText);

      const walker = document.createTreeWalker(this.revealCopy, NodeFilter.SHOW_TEXT);
      const textNodes = [];
      let currentNode = walker.nextNode();

      while (currentNode) {
        if (currentNode.nodeValue.trim()) textNodes.push(currentNode);
        currentNode = walker.nextNode();
      }

      textNodes.forEach((textNode) => {
        const isAccent = Boolean(textNode.parentElement && textNode.parentElement.closest('strong'));
        const fragment = document.createDocumentFragment();

        Array.from(textNode.nodeValue).forEach((character) => {
          if (/\s/.test(character)) {
            fragment.appendChild(document.createTextNode(character));
            return;
          }

          const span = document.createElement('span');
          span.className = `moz-rtf-story__reveal-char${isAccent ? ' is-accent' : ''}`;
          span.setAttribute('aria-hidden', 'true');
          span.textContent = character;
          fragment.appendChild(span);
        });

        textNode.replaceWith(fragment);
      });

      this.revealCopy.dataset.split = 'true';
      this.revealChars = Array.from(this.revealCopy.querySelectorAll('.moz-rtf-story__reveal-char'));
      this.lastLitIndex = -1;

      if (!this.motionQuery.matches) {
        this.setLitIndex(this.revealChars.length - 1);
      } else if (this.revealNearViewport) {
        this.updateReveal(window.innerHeight);
      }
    }

    bind() {
      const { signal } = this.abortController;
      const requestUpdate = (force = false) => {
        if (!force && !this.isNearViewport) return;
        if (!force && !this.motionQuery.matches && this.staticApplied) return;
        if (this.frame) return;
        this.frame = window.requestAnimationFrame(() => {
          this.frame = 0;
          this.update();
        });
      };

      window.addEventListener('scroll', () => {
        this.isPageScrolling = true;
        if (this.scrollIdleTimer) window.clearTimeout(this.scrollIdleTimer);
        this.scrollIdleTimer = window.setTimeout(() => {
          this.scrollIdleTimer = 0;
          this.isPageScrolling = false;
          this.pumpImageWarmQueue();
        }, 180);
        requestUpdate();
      }, { passive: true, signal });
      window.addEventListener('resize', () => {
        this.staticApplied = false;
        this.mobileCardCenters = null;
        this.activeCardRect = null;
        this.maxConcurrentImageWarms = window.innerWidth < 990 ? 1 : 2;
        this.measureTracks();
        this.pumpImageWarmQueue();
        requestUpdate(true);
      }, { passive: true, signal });
      this.motionQuery.addEventListener('change', () => {
        this.staticApplied = false;
        requestUpdate(true);
      }, { signal });
      this.mobileMotionQuery.addEventListener('change', () => {
        this.staticApplied = false;
        this.syncMobileObservers();
        requestUpdate(true);
      }, { signal });

      if ('IntersectionObserver' in window) {
        this.viewportObserver = new IntersectionObserver((entries) => {
          this.isNearViewport = entries.some((entry) => entry.isIntersecting);
          if (this.isNearViewport) {
            this.measureTracks();
            requestUpdate(true);
          }
          else if (this.frame) {
            window.cancelAnimationFrame(this.frame);
            this.frame = 0;
          }
        }, { rootMargin: '100% 0px', threshold: 0 });
        this.viewportObserver.observe(this.root);

        this.phaseObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.target === this.stageTrack) this.stageNearViewport = entry.isIntersecting;
            if (entry.target === this.revealTrack) {
              this.revealNearViewport = entry.isIntersecting;
              if (entry.isIntersecting) this.prepareReveal();
            }
          });
          requestUpdate(true);
        }, { rootMargin: '40% 0px', threshold: 0 });
        if (this.stageTrack) this.phaseObserver.observe(this.stageTrack);
        if (this.revealTrack) this.phaseObserver.observe(this.revealTrack);

        this.preloadObserver = new IntersectionObserver((entries, observer) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.warmImages(0);
          observer.disconnect();
        }, { rootMargin: '200% 0px', threshold: 0 });
        this.preloadObserver.observe(this.stageTrack || this.root);
      } else {
        this.warmImages(0);
      }

      if ('ResizeObserver' in window) {
        this.layoutObserver = new ResizeObserver(() => {
          if (this.layoutFrame) return;
          this.layoutFrame = window.requestAnimationFrame(() => {
            this.layoutFrame = 0;
            this.measureTracks();
            requestUpdate(true);
          });
        });
        if (this.stageTrack) this.layoutObserver.observe(this.stageTrack);
        if (this.revealTrack) this.layoutObserver.observe(this.revealTrack);
      }

      this.stepButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const targetIndex = Number(button.dataset.storyJump || 0);
          this.warmImages(targetIndex);
          this.scrollToIndex(targetIndex);
        }, { signal });
      });

      if (this.fan) {
        this.fan.addEventListener('scroll', () => {
          if (this.motionQuery.matches || this.mobileFrame) return;
          this.mobileFrame = window.requestAnimationFrame(() => {
            this.mobileFrame = 0;
            this.updateMobileCardStep();
          });
        }, { passive: true, signal });

        if ('PointerEvent' in window) {
          this.fan.addEventListener('pointermove', (event) => this.queuePointerMove(event), { passive: true, signal });
          this.fan.addEventListener('pointerleave', () => {
            if (!this.dragState) this.resetPointerEffects();
          }, { signal });
          this.fan.addEventListener('pointerdown', (event) => this.startDrag(event), { signal });
          this.fan.addEventListener('pointerup', (event) => this.finishDrag(event), { signal });
          this.fan.addEventListener('pointercancel', () => this.cancelDrag(), { signal });
        } else {
          this.fan.addEventListener('mousemove', (event) => this.queuePointerMove(event), { passive: true, signal });
          this.fan.addEventListener('mouseleave', () => {
            if (!this.dragState) this.resetPointerEffects();
          }, { signal });
          this.fan.addEventListener('mousedown', (event) => this.startDrag(event), { signal });
          window.addEventListener('mousemove', (event) => this.queuePointerMove(event), { passive: true, signal });
          window.addEventListener('mouseup', (event) => this.finishDrag(event), { signal });
        }
      }

      this.syncMobileObservers();
    }

    syncMobileObservers() {
      this.mobileObserver?.disconnect();
      this.mobileObserver = null;

      if (!this.mobileMotionQuery.matches || !('IntersectionObserver' in window)) {
        this.root.classList.remove('is-mobile-enhanced');
        this.root.classList.add('is-mobile-stage-visible', 'is-mobile-copy-visible');
        return;
      }

      this.root.classList.add('is-mobile-enhanced');
      this.root.classList.remove('is-mobile-stage-visible', 'is-mobile-copy-visible');

      this.mobileObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          if (entry.target === this.stageTrack) this.root.classList.add('is-mobile-stage-visible');
          if (entry.target === this.revealTrack) this.root.classList.add('is-mobile-copy-visible');
          observer.unobserve(entry.target);
        });
      }, { threshold: 0.18, rootMargin: '0px 0px -8% 0px' });

      const observeOrShow = (target, visibleClass) => {
        if (!target) return;
        if (target.getBoundingClientRect().bottom <= 0) {
          this.root.classList.add(visibleClass);
          return;
        }
        this.mobileObserver.observe(target);
      };

      observeOrShow(this.stageTrack, 'is-mobile-stage-visible');
      observeOrShow(this.revealTrack, 'is-mobile-copy-visible');
    }

    setActiveStep(activeIndex) {
      if (activeIndex === this.lastStepIndex) return;
      this.stepButtons.forEach((button, index) => {
        const isActive = index === activeIndex;
        button.classList.toggle('is-active', isActive);
        if (isActive) button.setAttribute('aria-current', 'step');
        else button.removeAttribute('aria-current');
      });
      this.lastStepIndex = activeIndex;
    }

    updateStepNumber(activeIndex) {
      if (!this.number) return;

      const nextNumber = String(activeIndex + 1).padStart(2, '0');
      if (this.number.textContent === nextNumber) return;
      this.number.textContent = nextNumber;

      if (!this.motionQuery.matches && !this.mobileMotionQuery.matches) return;

      this.numberAnimation?.cancel();
      this.numberAnimation = this.number.animate?.([
        {
          opacity: 0.24,
          transform: 'translate3d(0, 0.18em, 0)',
          backgroundPosition: '100% 50%',
        },
        {
          opacity: 1,
          transform: 'translate3d(0, 0, 0)',
          backgroundPosition: '38% 50%',
          offset: 0.58,
        },
        {
          opacity: 1,
          transform: 'translate3d(0, 0, 0)',
          backgroundPosition: '100% 50%',
        },
      ], {
        duration: 540,
        easing: 'cubic-bezier(0.16, 0.8, 0.24, 1)',
      }) || null;
    }

    setStyle(element, property, value) {
      if (!element) return;
      if (property.startsWith('--')) {
        if (element.style.getPropertyValue(property) !== value) element.style.setProperty(property, value);
        return;
      }
      if (element.style[property] !== value) element.style[property] = value;
    }

    warmImages(centerIndex) {
      if (this.warmedCenters.has(centerIndex)) return;
      this.warmedCenters.add(centerIndex);

      const start = Math.max(centerIndex - 1, 0);
      const end = Math.min(centerIndex + 1, this.cards.length - 1);

      for (let index = start; index <= end; index += 1) {
        const image = this.cards[index]?.querySelector('img');
        if (!image) continue;
        this.queueImageWarm(image, index === centerIndex);
      }
    }

    queueImageWarm(image, prioritize = false) {
      if (!image) return;
      if (prioritize) image.dataset.mozWarmPriority = 'high';
      if (this.queuedImages.has(image)) {
        if (prioritize) {
          const queueIndex = this.imageWarmQueue.indexOf(image);
          if (queueIndex > 0) {
            this.imageWarmQueue.splice(queueIndex, 1);
            this.imageWarmQueue.unshift(image);
          }
        }
        return;
      }
      this.queuedImages.add(image);
      if (prioritize) this.imageWarmQueue.unshift(image);
      else this.imageWarmQueue.push(image);
      this.pumpImageWarmQueue();
    }

    pumpImageWarmQueue() {
      if (this.isPageScrolling || document.hidden) return;
      while (this.imageWarmActive < this.maxConcurrentImageWarms && this.imageWarmQueue.length) {
        const image = this.imageWarmQueue.shift();
        this.imageWarmActive += 1;
        image.loading = 'eager';
        image.fetchPriority = image.dataset.mozWarmPriority === 'high' ? 'high' : 'auto';

        const ready = typeof image.decode === 'function'
          ? image.decode()
          : image.complete
            ? Promise.resolve()
            : new Promise((resolve) => {
              image.addEventListener('load', resolve, { once: true });
              image.addEventListener('error', resolve, { once: true });
            });

        Promise.resolve(ready).catch(() => {}).finally(() => {
          this.imageWarmActive -= 1;
          this.pumpImageWarmQueue();
        });
      }
    }

    getMobileCardIndex() {
      if (!this.fan || !this.cards.length) return 0;
      const fanCenter = this.fan.scrollLeft + this.fan.clientWidth / 2;
      let closestIndex = 0;
      let closestDistance = Infinity;

      if (!this.mobileCardCenters) {
        this.mobileCardCenters = this.cards.map((card) => card.offsetLeft + card.offsetWidth / 2);
      }

      this.mobileCardCenters.forEach((cardCenter, index) => {
        const distance = Math.abs(cardCenter - fanCenter);
        if (distance >= closestDistance) return;
        closestDistance = distance;
        closestIndex = index;
      });

      return closestIndex;
    }

    setStaticCardStep(activeIndex) {
      const safeIndex = clamp(activeIndex, 0, this.cards.length - 1);
      this.cards.forEach((card, index) => card.classList.toggle('is-active', index === safeIndex));
      this.updateStepNumber(safeIndex);
      this.warmImages(safeIndex);
      this.setActiveStep(safeIndex);
      if (this.progressBar) {
        this.setStyle(this.progressBar, 'transform', `scaleX(${((safeIndex + 1) / this.cards.length).toFixed(4)})`);
      }
      this.lastActiveIndex = safeIndex;
    }

    updateMobileCardStep() {
      if (this.motionQuery.matches) return;
      const activeIndex = this.getMobileCardIndex();
      if (activeIndex === this.lastActiveIndex) return;

      this.setStaticCardStep(activeIndex);
    }

    resetPointerEffects() {
      this.surfaces.forEach((surface) => {
        if (!surface) return;
        this.setStyle(surface, '--tilt-x', '0deg');
        this.setStyle(surface, '--tilt-y', '0deg');
        this.setStyle(surface, '--image-x', '0px');
        this.setStyle(surface, '--image-y', '0px');
        this.setStyle(surface, '--drag-x', '0px');
        this.setStyle(surface, '--glare-opacity', '0');
      });
    }

    queuePointerMove(event) {
      this.pendingPointerPoint = { clientX: event.clientX, clientY: event.clientY };
      if (this.pointerFrame) return;

      this.pointerFrame = window.requestAnimationFrame(() => {
        this.pointerFrame = 0;
        const point = this.pendingPointerPoint;
        this.pendingPointerPoint = null;
        if (point) this.handlePointerMove(point);
      });
    }

    handlePointerMove(event) {
      if (!this.motionQuery.matches) return;

      if (this.dragState) {
        this.dragState.currentX = event.clientX;
        this.dragState.currentY = event.clientY;
        const deltaX = this.dragState.currentX - this.dragState.startX;
        const distance = Math.abs(deltaX);
        if (distance > 8) this.fan?.classList.add('is-dragging');
        const draggedSurface = this.surfaces[this.dragState.startIndex];
        draggedSurface?.style.setProperty('--drag-x', `${clamp(deltaX * 0.18, -38, 38).toFixed(2)}px`);
      }

      const activeCard = this.cards[this.lastActiveIndex];
      const surface = this.surfaces[this.lastActiveIndex];
      if (!activeCard || !surface) return;

      const rect = this.activeCardRect || activeCard.getBoundingClientRect();
      this.activeCardRect = rect;
      const isInside = event.clientX >= rect.left && event.clientX <= rect.right
        && event.clientY >= rect.top && event.clientY <= rect.bottom;

      if (!isInside) {
        surface.style.setProperty('--tilt-x', '0deg');
        surface.style.setProperty('--tilt-y', '0deg');
        surface.style.setProperty('--image-x', '0px');
        surface.style.setProperty('--image-y', '0px');
        surface.style.setProperty('--glare-opacity', '0');
        return;
      }

      const x = clamp((event.clientX - rect.left) / rect.width);
      const y = clamp((event.clientY - rect.top) / rect.height);
      const centeredX = x - 0.5;
      const centeredY = y - 0.5;

      surface.style.setProperty('--tilt-x', `${(-centeredY * 7).toFixed(2)}deg`);
      surface.style.setProperty('--tilt-y', `${(centeredX * 9).toFixed(2)}deg`);
      surface.style.setProperty('--image-x', `${(-centeredX * 12).toFixed(2)}px`);
      surface.style.setProperty('--image-y', `${(-centeredY * 10).toFixed(2)}px`);
      surface.style.setProperty('--glare-x', `${(x * 100).toFixed(1)}%`);
      surface.style.setProperty('--glare-y', `${(y * 100).toFixed(1)}%`);
      surface.style.setProperty('--glare-opacity', '0.72');
    }

    startDrag(event) {
      if (this.dragState || !this.motionQuery.matches || event.button !== 0 || event.target.closest('a, button')) return;

      const pointerId = event.pointerId ?? 'mouse';

      this.dragState = {
        pointerId,
        startX: event.clientX,
        startY: event.clientY,
        currentX: event.clientX,
        currentY: event.clientY,
        startIndex: Math.max(this.lastActiveIndex, 0),
      };
      if (typeof pointerId === 'number') this.fan?.setPointerCapture?.(pointerId);
    }

    finishDrag(event) {
      const pointerId = event.pointerId ?? 'mouse';
      if (!this.dragState || this.dragState.pointerId !== pointerId) return;

      const deltaX = event.clientX - this.dragState.startX;
      const deltaY = event.clientY - this.dragState.startY;
      const startIndex = this.dragState.startIndex;
      this.cancelDrag();

      if (Math.abs(deltaX) < 64 || Math.abs(deltaX) < Math.abs(deltaY)) return;
      const targetIndex = clamp(startIndex + (deltaX < 0 ? 1 : -1), 0, this.cards.length - 1);
      this.scrollToIndex(targetIndex);
    }

    cancelDrag() {
      if (typeof this.dragState?.pointerId === 'number' && this.fan?.hasPointerCapture?.(this.dragState.pointerId)) {
        this.fan.releasePointerCapture(this.dragState.pointerId);
      }
      this.dragState = null;
      this.fan?.classList.remove('is-dragging');
      this.resetPointerEffects();
    }

    update() {
      if (!this.stageTrack || !this.cards.length) return;

      if (!this.motionQuery.matches) {
        if (this.staticApplied) return;
        this.root.classList.add('is-static');
        this.stage?.classList.remove('is-pinned');
        this.revealStage?.classList.remove('is-pinned');
        this.stage?.style.removeProperty('transform');
        const staticActiveIndex = this.getMobileCardIndex();
        this.setStaticCardStep(staticActiveIndex);
        this.resetPointerEffects();
        this.cards.forEach((card, index) => {
          card.classList.toggle('is-active', index === staticActiveIndex);
          card.removeAttribute('aria-hidden');
          card.style.removeProperty('z-index');
          card.style.removeProperty('opacity');
          card.style.removeProperty('transform');
          card.style.removeProperty('visibility');
          card.style.removeProperty('pointer-events');
          card.style.removeProperty('--card-copy-opacity');
          card.style.removeProperty('--card-copy-shift');
          if (this.links[index]) this.links[index].removeAttribute('tabindex');
        });
        this.cardInteractionStates = [];
        this.cardRenderStates = [];
        this.revealChars?.forEach((character) => character.classList.add('is-lit'));
        this.lastLitIndex = (this.revealChars?.length || 0) - 1;
        this.root.style.setProperty('--moz-handoff-opacity', '1');
        this.root.style.setProperty('--moz-handoff-glow', '1');
        this.root.style.setProperty('--moz-handoff-shift', '0px');
        this.root.style.setProperty('--moz-reveal-lift', '0vh');
        this.root.style.setProperty('--moz-reveal-opacity', '1');
        this.staticApplied = true;
        return;
      }

      this.staticApplied = false;
      this.root.classList.remove('is-static');
      this.activeCardRect = null;
      const viewportHeight = window.innerHeight;
      if (!this.stageNearViewport) {
        this.updateReveal(viewportHeight);
        return;
      }
      const scrollTop = window.scrollY || window.pageYOffset || 0;
      const trackTop = this.stageMetrics
        ? this.stageMetrics.top - scrollTop
        : this.stageTrack.getBoundingClientRect().top;
      const trackHeight = this.stageMetrics?.height || this.stageTrack.offsetHeight || 1;
      const trackBottom = trackTop + trackHeight;
      this.stage?.classList.toggle('is-pinned', trackTop <= 1 && trackBottom >= viewportHeight - 1);
      const stageDistance = Math.max(trackHeight - viewportHeight, 1);
      const stageProgress = clamp(-trackTop / stageDistance);
      const segmentCount = Math.max(this.cards.length - 1, 1);
      const rawPosition = stageProgress * segmentCount;
      const isFinalStep = stageProgress >= 0.9999 || this.cards.length === 1;
      const segmentIndex = isFinalStep
        ? this.cards.length - 1
        : Math.min(Math.floor(rawPosition), this.cards.length - 2);
      const nextIndex = isFinalStep ? null : segmentIndex + 1;
      const segmentProgress = isFinalStep ? 0 : rawPosition - segmentIndex;

      /*
       * A deliberate four-beat handoff keeps two products from competing:
       * current hold -> current exits completely -> breathing room -> next enters.
       * ENTER_START is later than EXIT_END by design, so their visible ranges
       * can never overlap even on fast trackpad or touchpad scrolling.
       */
      const EXIT_START = 0.18;
      const EXIT_END = 0.44;
      const ENTER_START = 0.56;
      const ENTER_END = 0.84;
      const exitProgress = isFinalStep
        ? 0
        : smoothstep(clamp((segmentProgress - EXIT_START) / (EXIT_END - EXIT_START)));
      const enterProgress = isFinalStep
        ? 0
        : smoothstep(clamp((segmentProgress - ENTER_START) / (ENTER_END - ENTER_START)));
      const activeIndex = nextIndex !== null && enterProgress > 0.02 ? nextIndex : segmentIndex;

      if (nextIndex !== null) this.warmImages(nextIndex);

      if (activeIndex !== this.lastActiveIndex) this.resetPointerEffects();

      this.cards.forEach((card, index) => {
        let x = index < segmentIndex ? -76 : 86;
        let y = 12;
        let rotation = index < segmentIndex ? -14 : 18;
        let scale = 0.9;
        let opacity = 0;
        let copyOpacity = 0;
        let copyShift = 28;

        if (index === segmentIndex) {
          x = lerp(18, -76, exitProgress);
          y = lerp(0, 8, exitProgress);
          rotation = lerp(0, -14, exitProgress);
          scale = lerp(1, 0.9, exitProgress);
          opacity = 1 - exitProgress;
          copyOpacity = 1 - smoothstep(clamp(exitProgress / 0.72));
          copyShift = lerp(0, 22, exitProgress);
        } else if (index === nextIndex) {
          x = lerp(86, 18, enterProgress);
          y = lerp(12, 0, enterProgress);
          rotation = lerp(18, 0, enterProgress);
          scale = lerp(0.9, 1, enterProgress);
          opacity = enterProgress;
          copyOpacity = smoothstep(clamp((enterProgress - 0.42) / 0.58));
          copyShift = lerp(26, 0, copyOpacity);
        }

        const isActive = index === activeIndex;
        const isReadable = isActive && opacity > 0.48;

        const transform = `translate3d(calc(-50% + ${x}vw), calc(-50% + ${y}vh), 0) rotate(${rotation}deg) scale(${scale})`;
        const opacityValue = opacity.toFixed(3);
        const visibilityValue = opacity > 0.001 ? 'visible' : 'hidden';
        const pointerValue = isReadable ? 'auto' : 'none';
        const zIndexValue = String(index === nextIndex ? 22 : 20);
        const copyOpacityValue = copyOpacity.toFixed(3);
        const copyShiftValue = `${copyShift.toFixed(2)}px`;
        const renderState = `${transform}|${opacityValue}|${visibilityValue}|${pointerValue}|${zIndexValue}|${copyOpacityValue}|${copyShiftValue}`;

        if (this.cardRenderStates[index] !== renderState) {
          this.setStyle(card, 'transform', transform);
          this.setStyle(card, 'opacity', opacityValue);
          this.setStyle(card, 'visibility', visibilityValue);
          this.setStyle(card, 'pointerEvents', pointerValue);
          this.setStyle(card, 'zIndex', zIndexValue);
          this.setStyle(card, '--card-copy-opacity', copyOpacityValue);
          this.setStyle(card, '--card-copy-shift', copyShiftValue);
          this.cardRenderStates[index] = renderState;
        }

        const interactionState = `${isActive}:${isReadable}`;
        if (this.cardInteractionStates[index] !== interactionState) {
          card.classList.toggle('is-active', isActive);
          card.setAttribute('aria-hidden', isReadable ? 'false' : 'true');

          if (this.links[index]) {
            if (isReadable) this.links[index].removeAttribute('tabindex');
            else this.links[index].setAttribute('tabindex', '-1');
          }
          this.cardInteractionStates[index] = interactionState;
        }
      });

      if (activeIndex !== this.lastActiveIndex && this.number) {
        this.warmImages(activeIndex);
        this.updateStepNumber(activeIndex);
        this.lastActiveIndex = activeIndex;
      }
      this.setActiveStep(activeIndex);

      if (this.progressBar) {
        this.setStyle(this.progressBar, 'transform', `scaleX(${stageProgress.toFixed(4)})`);
      }

      this.updateReveal(viewportHeight);
    }

    updateReveal(viewportHeight) {
      if (!this.revealNearViewport || !this.revealTrack || !this.revealChars?.length) return;

      const scrollTop = window.scrollY || window.pageYOffset || 0;
      const revealTop = this.revealMetrics
        ? this.revealMetrics.top - scrollTop
        : this.revealTrack.getBoundingClientRect().top;
      const revealHeight = this.revealMetrics?.height || this.revealTrack.offsetHeight || 1;
      const revealBottom = revealTop + revealHeight;
      this.revealStage?.classList.toggle('is-pinned', revealTop <= 1 && revealBottom >= viewportHeight * 0.58);
      const revealDistance = Math.max(revealHeight - viewportHeight, 1);
      const revealProgress = clamp(-revealTop / revealDistance);
      const litPosition = revealProgress * (this.revealChars.length + 18) - 4;
      const handoffProgress = clamp((revealProgress - 0.62) / 0.38);
      const releaseProgress = clamp((viewportHeight - revealBottom) / (viewportHeight * 0.42));
      const handoffOpacity = handoffProgress * (1 - releaseProgress);

      this.setStyle(this.root, '--moz-handoff-opacity', handoffOpacity.toFixed(3));
      this.setStyle(this.root, '--moz-handoff-glow', handoffProgress.toFixed(3));
      this.setStyle(this.root, '--moz-handoff-shift', `${((1 - handoffProgress) * 18).toFixed(2)}px`);
      this.setStyle(this.root, '--moz-reveal-lift', `${(-handoffProgress * 6.5).toFixed(2)}vh`);
      this.setStyle(this.root, '--moz-reveal-opacity', `${(1 - releaseProgress).toFixed(3)}`);

      this.setLitIndex(Math.floor(litPosition));
    }

    setLitIndex(nextIndex) {
      if (!this.revealChars?.length) return;
      const safeIndex = clamp(nextIndex, -1, this.revealChars.length - 1);
      if (safeIndex === this.lastLitIndex) return;

      if (safeIndex > this.lastLitIndex) {
        for (let index = this.lastLitIndex + 1; index <= safeIndex; index += 1) {
          this.revealChars[index]?.classList.add('is-lit');
        }
      } else {
        for (let index = this.lastLitIndex; index > safeIndex; index -= 1) {
          this.revealChars[index]?.classList.remove('is-lit');
        }
      }

      this.lastLitIndex = safeIndex;
    }

    scrollToCard(card) {
      const index = Number(card.dataset.storyIndex || 0);
      this.scrollToIndex(index);
    }

    scrollToIndex(index) {
      const safeIndex = clamp(index, 0, this.cards.length - 1);

      if (!this.motionQuery.matches) {
        const card = this.cards[safeIndex];
        if (!this.fan || !card) return;
        const targetLeft = card.offsetLeft - (this.fan.clientWidth - card.offsetWidth) / 2;
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.fan.scrollTo({
          left: Math.max(0, targetLeft),
          behavior: reducedMotion ? 'auto' : 'smooth',
        });
        this.setStaticCardStep(safeIndex);
        return;
      }

      if (!this.stageTrack) return;
      const distance = Math.max((this.stageMetrics?.height || this.stageTrack.offsetHeight) - window.innerHeight, 0);
      const target = (this.stageMetrics?.top || (this.stageTrack.getBoundingClientRect().top + window.scrollY))
        + distance * (safeIndex / Math.max(this.cards.length - 1, 1));
      window.scrollTo({ top: target, behavior: 'smooth' });
    }

    destroy() {
      this.abortController.abort();
      if (this.frame) window.cancelAnimationFrame(this.frame);
      if (this.mobileFrame) window.cancelAnimationFrame(this.mobileFrame);
      if (this.pointerFrame) window.cancelAnimationFrame(this.pointerFrame);
      if (this.layoutFrame) window.cancelAnimationFrame(this.layoutFrame);
      if (this.scrollIdleTimer) window.clearTimeout(this.scrollIdleTimer);
      if (this.revealPrepareHandle) {
        if (this.revealPrepareUsesIdle && 'cancelIdleCallback' in window) {
          window.cancelIdleCallback(this.revealPrepareHandle);
        } else {
          window.clearTimeout(this.revealPrepareHandle);
        }
      }
      this.numberAnimation?.cancel();
      this.mobileObserver?.disconnect();
      this.viewportObserver?.disconnect();
      this.phaseObserver?.disconnect();
      this.preloadObserver?.disconnect();
      this.layoutObserver?.disconnect();
      this.cancelDrag();
      this.stage?.classList.remove('is-pinned');
      this.revealStage?.classList.remove('is-pinned');
      this.stage?.style.removeProperty('transform');
      this.root.removeAttribute('data-story-initialized');
    }
  }

  const instances = new Map();

  const initWithin = (scope) => {
    const roots = [];
    if (scope.matches?.(SELECTOR)) roots.push(scope);
    roots.push(...scope.querySelectorAll?.(SELECTOR) || []);

    roots.forEach((root) => {
      if (root.dataset.storyInitialized === 'true') return;
      root.dataset.storyInitialized = 'true';
      const instance = new MoztechRtfStoryInstance(root);
      instances.set(root.dataset.sectionId, instance);
    });
  };

  const destroyWithin = (scope) => {
    const root = scope.matches?.(SELECTOR) ? scope : scope.querySelector?.(SELECTOR);
    if (!root) return;
    const instance = instances.get(root.dataset.sectionId);
    instance?.destroy();
    instances.delete(root.dataset.sectionId);
  };

  document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const card = event.target.closest?.('[data-story-card]');
    if (!card) return;
    const root = card.closest(SELECTOR);
    instances.get(root?.dataset.sectionId)?.scrollToCard(card);
  });

  window.MoztechRtfStory = { initWithin, destroyWithin, instances };
  initWithin(document);
})();
